import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { inIframe, pathJoin, skipTenantInterceptor, stripHtml, updateStore } from '@asamco/utils';
import { BehaviorSubject } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { AsamcoAuthService } from '../auth/asamco-auth.service';
import { AppSettings } from '../configuration/appsettings';
import { IFrameChildService } from '../iframe/i-frame-child.service';
import { IFrameParentService } from '../iframe/i-frame-parent.service';
import { ofTypeOld } from '../iframe/iframe-message-rxjs';
import { MessageType } from '../iframe/message-type.enum';
import { NotificationHandler, SIGNALR_NOTIFICATION_HANDLER } from './notification-handler.interface';
import { Notification, NotificationData } from './notification.interface';
import { UserNotification } from './user-notification.interface';

/** Amount of days to load notification history for */
const nrOfDaysNotificationHistory = 7;

@Injectable({
	providedIn: 'root',
})
export class NotificationService {
	private notifications = new BehaviorSubject<UserNotification<NotificationData>[]>([]);

	/** All user notifications, sorted desc */
	public notifications$ = this.notifications.pipe(map(notifications => this.sortNotifications(notifications)));
	public unread$ = this.notifications$.pipe(map(notifications => notifications.some(n => !n.isRead)));

	private isInnerApp: boolean = false;

	constructor(
		private http: HttpClient,
		private auth: AsamcoAuthService,
		@Inject(SIGNALR_NOTIFICATION_HANDLER) private notificationHandlers: NotificationHandler<any>[],
		private appSettings: AppSettings,
		private parentService: IFrameParentService,
		childService: IFrameChildService
	) {
		if (window.Notification && window.Notification.permission === 'default') {
			window.Notification.requestPermission();
		}

		this.isInnerApp = inIframe();

		// If this is the outer app listen for notification from inner app
		if (this.isInnerApp) {
			childService.incomingMessages$.pipe(ofTypeOld(MessageType.notificationClick)).subscribe(m => {
				m.payload.data.fromIFrame = false;
				this.onNotificationClick(m.payload);
			});
		} else {
			parentService.incomingMessages$.pipe(ofTypeOld(MessageType.notification)).subscribe(m => {
				this.handle(m.payload, true);
			});
			this.loadHistory();
		}
	}

	/**
	 * Sort notifications desc
	 * @param notifications
	 */
	sortNotifications(notifications: UserNotification<NotificationData>[]): UserNotification<NotificationData>[] {
		return notifications.sort((a, b) => (a.createdOnDate > b.createdOnDate ? -1 : 1));
	}

	/**
	 * Load notifications of last 7 days
	 */
	private loadHistory() {
		const loadHistory$ = this.auth.isLoggedIn$.pipe(
			filter(isLoggedIn => isLoggedIn),
			switchMap(_ => this.http.get(`notification/history/${nrOfDaysNotificationHistory}`, { headers: skipTenantInterceptor() })),
			tap((notifications: Notification<NotificationData>[]) => {
				notifications.forEach(n => this.handle(n, false));
			}),
			take(1),
			shareReplay()
		);

		loadHistory$.pipe(take(1)).subscribe({error: console.error});

		return loadHistory$;
	}

	/**
	 * Pass notifications to the correct handlers and add to the notification list if it should be added to the dropdown
	 * @param notification
	 */
	handle(notification: Notification<NotificationData>, isNewNotification: boolean) {
		this.notificationHandlers
			.filter(handler => handler.dataType === '*' || handler.dataType === notification.dataType)
			.forEach(handler => {
				if (isNewNotification) {
					handler.handleNotification(notification);
				}
				const userNotification = handler.formatUserNotification(notification);

				if (userNotification != null) {
					const newNotifications = updateStore(this.notifications.getValue(), userNotification, 'id');
					this.notifications.next(newNotifications);

					if (isNewNotification) {
						this.showWindowsNotification(userNotification);
					}
				}
			});
	}

	/**
	 * Shows a desktop notification to the user
	 * @param userNotification
	 */
	showWindowsNotification(userNotification: UserNotification<any>) {
		if (window.Notification.permission === 'granted') {
			const not = new window.Notification(userNotification.title, {
				body: stripHtml(userNotification.htmlBody),
				icon: pathJoin([this.appSettings.deployUrl, 'assets/favicon-32x32.png']),
			});

			not.onclick = () => this.onNotificationClick(userNotification);
		}
	}

	/**
	 * Marks notification as read and call the correct action handlers
	 * @param notification
	 */
	onNotificationClick(notification: UserNotification<NotificationData>) {
		const fromIFrame = notification.data.fromIFrame === true;

		// If is inner app or when the notification is from the outer app itself
		// call mark as read to make sure the backend is called to mark the notification
		// is marked as read
		if (this.isInnerApp || (!this.isInnerApp && !fromIFrame)) {
			this.markAsRead(notification);
		}
		// Else just mark it as read, the inner app notification service will make
		// a request to its own backend to mark the notification as read.
		else {
			this.markNotificationAsReadById(notification.id);
		}

		if (notification.isClickable) {
			if (fromIFrame) {
				this.parentService.send({
					type: MessageType.notificationClick,
					payload: notification,
				});
			} else {
				this.notificationHandlers
					.filter(handler => handler.dataType === '*' || handler.dataType === notification.dataType)
					.forEach(handler => {
						handler.onClick(notification);
					});
			}
		}
	}

	/**
	 * Mark all notifications as read
	 */
	markAllAsRead() {
		this.http
			.post(`notification/read`, null, { headers: skipTenantInterceptor() })
			.pipe(
				tap(_ => {
					const notifications: UserNotification<NotificationData>[] = this.notifications.getValue().map(n => ({
						...n,
						isRead: true,
					}));
					this.notifications.next(notifications);
				})
			)
			.subscribe({error: console.error});
	}

	/**
	 * Marks a notification as read
	 * @param notification
	 */
	markAsRead(notification: UserNotification<NotificationData>) {
		this.http
			.post(`notification/read/${notification.id}`, null, { headers: skipTenantInterceptor() })
			.pipe(
				tap(_ => {
					this.markNotificationAsReadById(notification.id);
				})
			)
			.subscribe({error: console.error});
	}

	/** Marks the notification as read by its ID */
	markNotificationAsReadById(id: string) {
		const notifications: UserNotification<NotificationData>[] = this.notifications.getValue().map(n => {
			if (n.id === id) {
				return {
					...n,
					isRead: true,
				};
			} else {
				return n;
			}
		});

		this.notifications.next(notifications);
	}
}
